import customToast from '../CustomNotification';
import Attendance from './components/pages/Attendance';
import FontCover from './components/pages/FontCover';
import HSBMI from './components/pages/HSBMI';
import HSBloodPressure from './components/pages/HSBloodPressure';
import HSDirectLDL from './components/pages/HSDirectLDL';
import HSHbA1c from './components/pages/HSHbA1c';
import HSLowHDL from './components/pages/HSLowHDL';
import HSTotalCholesterol from './components/pages/HSTotalCholesterol';
import HSTriglyceride from './components/pages/HSTriglyceride';
import LifestyleQuestionaireFinding from './components/pages/LifestyleQuestionaireFinding';
import QuickLook from './components/pages/QuickLook';
import TableContent from './components/pages/TableContent';
import ThankYou from './components/pages/ThankYou';
import { LIST_TABLE_CONTENTS_LABEL_DISPLAY } from './constants';
import { DownloadIcon } from '@/assets/svg';
import { urlLabel } from '@/enum/PermissionEnum';
import { API_ROOT } from '@/module/common';
import CRReportViewerDownload from '@/module/coporate-report-viewer-download';
import { LoadingOutlined } from '@ant-design/icons';
import { Box, CircularProgress, IconButton } from '@material-ui/core';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

const CRReportHTML = ({ accessToken, projectId }) => {
  const newAccessToken = accessToken || localStorage.getItem('accessToken');
  const query = new URLSearchParams(useLocation().search);
  const [loading, setLoading] = useState(false);
  const [corporateData, setCorporateData] = useState(null);
  const history = useHistory();

  const newPage = useRef(0);
  const [totalPage, setTotalPage] = useState([]);
  const [totalLifeStylePage, setTotalLifeStylePage] = useState(0);

  const projectID = projectId || query.get('projectId');

  const [downloading, setDownloading] = useState({});
  const [activeRow, setActiveRow] = useState(null);
  const handleDownload = async (projectId) => {
    if (downloading[projectId]) return; // Prevent multiple downloads
    setDownloading((prev) => ({ ...prev, [projectId]: true }));
    setActiveRow(projectId); // Render CRReportPDF for this row
  };

  // Page numbers are derived here because this is the only place that knows
  // which sections render and how many pages each one emits.
  const pageMap = useMemo(() => {
    const hs = corporateData?.healthScreenings;
    const hasOverView = !!corporateData?.overView;
    const sections = [
      ['quickLook', hasOverView && !!hs, 1],
      ['attendance', hasOverView, 1],
      [
        'bloodPressure',
        !!hs?.bloodPressure,
        hs?.bloodPressure?.trendings ? 2 : 1,
      ],
      ['bmi', !!hs?.bmi, hs?.bmi?.trendings ? 2 : 1],
      ['tc', !!hs?.tc, 1],
      ['ldl', !!hs?.ldl, hs?.ldl?.repeatParticipantTrendings != null ? 2 : 1],
      ['hdl', !!hs?.hdl, hs?.hdl?.repeatParticipantTrendings != null ? 2 : 1],
      ['triglyceride', !!hs?.triglyceride, hs?.triglyceride?.trendings ? 2 : 1],
      [
        'hbA1c',
        !!hs?.hbA1c,
        hs?.hbA1c?.trendings || hs?.hbA1c?.repeatParticipantTrendings != null
          ? 2
          : 1,
      ],
    ];

    const start = {};
    let cursor = 1;
    sections.forEach(([key, show, pages]) => {
      if (!show) return;
      start[key] = cursor;
      cursor += pages;
    });
    // The TOC lists the health-screening block as one entry.
    start.healthScreenings =
      start.bloodPressure ?? start.bmi ?? start.tc ?? start.ldl ?? start.hdl;

    const lastFixedPage = cursor - 1;
    const lifestyle = corporateData?.questionaires ? lastFixedPage + 1 : null;
    if (lifestyle != null) start.lifestyle = lifestyle;

    return {
      start,
      // lifestyle pages + the ThankYou page
      totalPages: lastFixedPage + totalLifeStylePage + 1,
    };
  }, [corporateData, totalLifeStylePage]);

  const addPaging = (label) => {
    newPage.current += 1;
    const key = label.replace(/\s+/g, '');
    setTotalPage((prevHistory) => [
      ...prevHistory,
      {
        key,
        label: LIST_TABLE_CONTENTS_LABEL_DISPLAY[key],
        page: newPage.current,
      },
    ]);
  };
  useEffect(() => {
    if (!newAccessToken || !projectID) {
      window.location.reload();

      history.push(
        `/${urlLabel.previewDownloadCRReport}?requestKey=${localStorage.getItem(
          'requestKey'
        )}&hrEmail=${localStorage.getItem('hrEmail')}`
      );
    }
    setLoading(true);
    fetch(`${API_ROOT}project-corporate-reports/${projectID}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${newAccessToken}`,
      },
    })
      .then((res) => {
        if (get(res, 'status') === 401) {
          customToast('error', 'Token was expired!');
          localStorage.removeItem('accessToken');

          window.location.href = `https://${
            process.env.REACT_APP_MINC_DOMAIN
          }/#/${
            urlLabel.previewDownloadCRReport
          }?requestKey=${localStorage.getItem(
            'requestKey'
          )}&hrEmail=${localStorage.getItem('hrEmail')}`;
          window.location.reload();
        } else if (get(res, 'status') === 200) {
          return res.json();
        }
      })
      .then((res) => {
        if (get(res, 'statusCode') === 200) {
          setLoading(false);
          setCorporateData(res?.data);
        } else {
          customToast('error', res.message);
        }
      })

      .catch((error) => {
        console.log('error', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAccessToken, projectID]);

  return (
    <Box position="relative" overflow="hidden">
      {!corporateData && loading ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box overflow="auto" bgcolor="#f5f5f5">
            <Box
              position="fixed"
              top={16}
              right={'calc(50% - 466px)'}
              zIndex={99}
              width={60}
              height={60}
              bgcolor="#fff"
              borderRadius={100}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <IconButton onClick={() => handleDownload(projectID)}>
                {downloading[projectID] ? (
                  <LoadingOutlined
                    width={60}
                    height={60}
                    style={{ fontSize: 24, color: '#6c6c6c' }}
                  />
                ) : (
                  <DownloadIcon width={60} height={60} />
                )}
              </IconButton>
            </Box>

            <FontCover corporateData={corporateData} />

            <TableContent pageMap={pageMap} totalPages={pageMap.totalPages} />

            {!corporateData?.overView ||
            !corporateData?.healthScreenings ? null : (
              <QuickLook
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.quickLook}
                totalPages={pageMap.totalPages}
              />
            )}

            {!corporateData?.overView ? null : (
              <Attendance
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.attendance}
                totalPages={pageMap.totalPages}
              />
            )}

            {!corporateData?.healthScreenings?.bloodPressure ? null : (
              <HSBloodPressure
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.bloodPressure}
                totalPages={pageMap.totalPages}
              />
            )}
            {!corporateData?.healthScreenings?.bmi ? null : (
              <HSBMI
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.bmi}
                totalPages={pageMap.totalPages}
              />
            )}

            {!corporateData?.healthScreenings?.tc ? null : (
              <HSTotalCholesterol
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.tc}
                totalPages={pageMap.totalPages}
              />
            )}

            {!corporateData?.healthScreenings?.ldl ? null : (
              <HSDirectLDL
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.ldl}
                totalPages={pageMap.totalPages}
              />
            )}

            {!corporateData?.healthScreenings?.hdl ? null : (
              <HSLowHDL
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.hdl}
                totalPages={pageMap.totalPages}
              />
            )}
            {!corporateData?.healthScreenings?.triglyceride ? null : (
              <HSTriglyceride
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.triglyceride}
                totalPages={pageMap.totalPages}
              />
            )}
            {!corporateData?.healthScreenings?.hbA1c ? null : (
              <HSHbA1c
                corporateData={corporateData}
                addPaging={addPaging}
                startPage={pageMap.start.hbA1c}
                totalPages={pageMap.totalPages}
              />
            )}
            {!corporateData?.questionaires ? null : (
              <LifestyleQuestionaireFinding
                corporateData={corporateData}
                setTotalLifeStylePage={setTotalLifeStylePage}
                startPage={pageMap.start.lifestyle}
                totalPages={pageMap.totalPages}
              />
            )}
            <ThankYou totalPages={pageMap.totalPages} />
          </Box>
          {newAccessToken && activeRow === projectID && (
            <CRReportViewerDownload
              accessToken={newAccessToken}
              downloading={downloading}
              resetData={() => {
                setDownloading({});
                setActiveRow(null);
              }}
              projectId={projectID}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CRReportHTML;
